import { useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import { useEmployeeRole } from './useEmployeeRole';
import { canResolve, type Capability } from './permissions';

/**
 * Phase 1 (custom roles) — live-aware capability check.
 *
 * Returns whether the *acting* employee may perform `cap`, consulting the flat
 * `employeeIdentity.capabilities` list returned by GET /api/v1/me when present,
 * and falling back to the static role matrix otherwise (see `canResolve`).
 *
 * This is the hook every UI gate should use going forward, so that owner edits
 * to a role's capability set (via the Roles page) hide/show UI immediately on
 * the next `/me` without a code change. `<RoleGate>` is built on top of it.
 *
 * Usage:
 *   const canManageOrg = useCan('settings.manage_org');
 */
export function useCan(cap: Capability): boolean {
  const role = useEmployeeRole();
  const capabilities = useSelector(
    (s: RootState) => s.auth?.agency?.employeeIdentity?.capabilities
  );
  return canResolve(capabilities, role, cap);
}

/**
 * Returns the acting employee's full live capability list (or `null`/`undefined`
 * when the backend hasn't supplied one yet). Useful for callers that need to
 * branch on several capabilities at once without N separate selector reads.
 */
export function useCapabilities(): string[] | null | undefined {
  return useSelector(
    (s: RootState) => s.auth?.agency?.employeeIdentity?.capabilities
  );
}
